import { motion } from 'framer-motion';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useT } from '@/lib/i18n';

interface LandingPageHeroProps {
  badge: string;
  title: string;
  highlight?: string;
  description: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export function LandingPageHero({
  badge,
  title,
  highlight,
  description,
  ctaLabel = 'Kostenloses Erstgespräch',
  ctaHref = '/consulting#contact',
}: LandingPageHeroProps) {
  const t = useT();
  const resolvedCtaLabel =
    ctaLabel === 'Kostenloses Erstgespräch'
      ? t('Kostenloses Erstgespräch', 'Free initial consultation')
      : ctaLabel;
  const secondaryLabel = t('Mehr zu unserem Consulting', 'More about our consulting');
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.15),transparent_50%)]" />
      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-4xl text-center"
        >
          <span className="inline-block rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            {badge}
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-foreground md:text-6xl">
            {title}
            {highlight && (
              <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                {highlight}
              </span>
            )}
          </h1>
          <p className="hero-description mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
            {description}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="group">
              <LocalizedLink to={ctaHref}>
                {resolvedCtaLabel}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </LocalizedLink>
            </Button>
            <Button asChild variant="outline" size="lg">
              <LocalizedLink to="/consulting">{secondaryLabel}</LocalizedLink>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}